import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { flowService } from 'v2/shared/services/flow-service'
import { flowQueryKeys } from 'v2/shared/utils/query-key-management'
import { vibeAgentLogger as logger } from 'v2/utils/logger'
import { errorToast } from 'v2/utils/toasts'
import { useAgentSettingsFormValues } from '../../hooks/use-agent-settings-form-values'
import { useFlowId } from '../../hooks/use-flow-id'
import { useTypedReactFlow } from '../../hooks/use-react-flow'
import { useFlowStore } from '../../store'
import type { FlowData } from '../../types'
import { useCanvasSync } from './canvas-sync'
import { useEvalSink } from './eval-sink'
import { notifyIfHidden } from './notify'
import { createResyncScheduler } from './resync-scheduler'
import { drainQueueHead, shouldAutoDrain, useVibeAgentStore } from './store'
import { resync } from './sync'
import * as transport from './transport'
import { type GateAnswer, isRunLive, isTerminalUpdate, UPDATE_TYPES, type VibeEvent } from './wire'
import { serializeVibeAgentWorkflow } from './workflow-serializer'

/**
 * The one Vibe Agent hook: send / answer gates / cancel / reset, plus mount
 * rehydration and stream_gap resync. Every stream frame flows through exactly
 * one pipeline: transport → store.dispatch (chat/run state) + canvas-sync
 * (canvas) + eval bridge (evaluation_update). There is no other writer.
 */

// Mirrors the backend's APPROVAL_AUTO_MESSAGE — a manual approve is an ordinary
// visible user turn with this text.
export const APPROVAL_MESSAGE = "Looks good to me! Let's build it!"

/** Copy for a stream-admission failure (non-409 4xx). The BE rejection body
 *  carries a curated message for known error codes; status fallbacks cover
 *  proxy-level 4xx with no usable body. */
const streamErrorMessage = (err: transport.SSEError): string => {
  if (err.serverMessage) return err.serverMessage
  if (err.status === 401 || err.status === 403) {
    return 'Your session has expired. Refresh the page and try again.'
  }
  if (err.status === 404) {
    return 'This build session no longer exists. Send a message to start a new build.'
  }
  return 'Connection to the build was lost. Retry to reattach.'
}

export const useVibeAgent = () => {
  const { id: phloId } = useFlowId()
  const { getNodes, getEdges } = useTypedReactFlow()
  const onEvalEvent = useEvalSink()
  const { onCanvasEvent, reconcileFromSnapshot, scheduleFlowDataInvalidate } = useCanvasSync()
  const queryClient = useQueryClient()
  const { getSettingsFormValues } = useAgentSettingsFormValues()

  const rehydratedFor = useRef<string | null>(null)
  // One-shot per flow: when the MOUNT snapshot reports a live background run,
  // open the panel so the build is visible on landing. Consumed on first fire —
  // later resyncs (gaps, true-ups) never reopen a panel the user closed.
  const autoOpenFor = useRef<string | null>(null)
  const lastFlowIdRef = useRef<string | null>(null)
  // Cleared on unmount so an in-flight resync that resolves afterwards does not
  // re-hydrate the reset store or re-attach a zombie reader.
  const aliveRef = useRef(true)

  // Latest-ref pattern: the viewer callbacks live for a whole run; they must
  // read current values without re-arming anything on re-render.
  // triggerResync is added to the bag after its declaration below — the bag
  // exists so `viewer` (memoized once) reads every collaborator at call time.
  const ref = useRef({
    phloId,
    getNodes,
    getEdges,
    onCanvasEvent,
    reconcileFromSnapshot,
    scheduleFlowDataInvalidate,
    onEvalEvent,
    triggerResync: async () => {},
  })

  // The wiring is a PURE fan-out: every frame goes to the three consumers,
  // each of which filters its own events (reducer / canvas-sync / eval-sink).
  // No update_type branches live here — transport owns stream_gap (dead
  // cursor ⇒ detach + onGap), and the terminal true-up is a predicate, not a
  // dispatch: the transcript persisted at finalization is authoritative, so
  // one snapshot fetch replaces any streaming drift.
  const viewer: transport.StreamCallbacks = useMemo(
    () => ({
      onEvent: (event: VibeEvent) => {
        useVibeAgentStore.getState().dispatch(event)
        ref.current.onCanvasEvent(event)
        ref.current.onEvalEvent(event)
        if (isTerminalUpdate(event.update_type)) void ref.current.triggerResync()
      },
      onGap: () => void ref.current.triggerResync(),
      onDone: () => {
        // Terminal event already reduced runStatus; ensure a stuck 'sending'
        // (server closed with no terminal frame) does not lock the composer.
        const s = useVibeAgentStore.getState()
        if (s.runStatus === 'sending') s.setRunStatus('idle')
      },
      onError: (err) => {
        logger.warn('[VIBE] stream error', { status: err.status, code: err.code })
        if (err.status === 409) {
          errorToast({ title: 'A build is already running for this flow. Attaching as viewer.' })
          void ref.current.triggerResync()
          return
        }
        useVibeAgentStore.getState().dispatch({
          update_type: UPDATE_TYPES.ERROR,
          payload: { message: streamErrorMessage(err) },
        })
      },
    }),
    [],
  )

  // Flow-scoped, liveness-guarded, coalescing resync — the loop lives in
  // resync-scheduler.ts. Deps read through the refs so the scheduler (created
  // once — `viewer` is memoized once) always sees the current scope.
  const { trigger: triggerResync } = useMemo(
    () =>
      createResyncScheduler({
        getPhloId: () => ref.current.phloId ?? null,
        isAlive: () => aliveRef.current,
        resync: (id, isCurrent) => resync(id, viewer, isCurrent),
        reconcile: (workflow) => ref.current.reconcileFromSnapshot(workflow),
        onStaleInterrupted: () => ref.current.scheduleFlowDataInvalidate(),
        onLiveRun: () => {
          const id = ref.current.phloId
          if (id && autoOpenFor.current === id) {
            autoOpenFor.current = null
            useFlowStore.getState().setShowVibeSidebar(true)
          }
        },
      }),
    [viewer],
  )

  ref.current = {
    phloId,
    getNodes,
    getEdges,
    onCanvasEvent,
    reconcileFromSnapshot,
    scheduleFlowDataInvalidate,
    onEvalEvent,
    triggerResync,
  }

  const buildRequestBody = useCallback(
    (userMessage: string, workflowOverride?: string) => {
      const { phloId: id, getNodes, getEdges } = ref.current
      const baseGlobalMeta = id
        ? queryClient.getQueryData<FlowData>(flowQueryKeys().flowData(id))?.global_meta
        : undefined
      const currentWorkflow =
        workflowOverride ??
        serializeVibeAgentWorkflow({
          nodes: getNodes(),
          edges: getEdges(),
          baseGlobalMeta,
        })
      const { agentSettings, systemPrompt, currentFlowName } = useFlowStore.getState()
      const { sessionId } = useVibeAgentStore.getState()
      return {
        user_message: userMessage,
        current_workflow: currentWorkflow,
        session_id: sessionId ?? undefined,
        phlo_uuid: id ?? undefined,
        agent_settings: agentSettings ? getSettingsFormValues() : undefined,
        system_prompt: systemPrompt?.prompt ?? undefined,
        // Without this the backend save names a brand-new flow "Untitled Flow".
        flow_name: currentFlowName ?? undefined,
      }
    },
    [queryClient, getSettingsFormValues],
  )

  /** Ordinary visible user turn. `workflowOverride` serves callers whose
   *  canvas is not the live ReactFlow state (existing-flow migration builds
   *  the workflow from flowData before the canvas settles). */
  const send = useCallback(
    (text: string, options?: { workflowOverride?: string }) => {
      const trimmed = text.trim()
      if (!trimmed) return
      const store = useVibeAgentStore.getState()
      store.appendPendingUserMessage(trimmed)
      // Deliberately does NOT clear the composer: the queue drain and
      // "Send next" dispatch STAGED text while the (now always-live) composer
      // may hold an unrelated draft mid-keystroke — wiping it here is data
      // loss. Composer-originated callers clear their own input.
      transport.startRun(buildRequestBody(trimmed, options?.workflowOverride), viewer)
    },
    [buildRequestBody, viewer],
  )

  // Queue drain: when a run completes with messages staged, dispatch the head
  // through the ordinary send path. Deferred a microtask so the terminal
  // event's own frame (canvas invalidate, resync scheduling) fully unwinds
  // before startRun swaps the viewer; head identity is re-checked after the
  // unwind so an edit/remove racing the drain wins. Re-entrancy is safe:
  // send() sets `sending`, which is not a drain trigger — the next `completed`
  // chains the next item.
  useEffect(() => {
    return useVibeAgentStore.subscribe((state, prev) => {
      if (
        !shouldAutoDrain(
          prev.runStatus,
          state.runStatus,
          state.gate,
          state.queuedMessages.length,
          state.queuePaused,
        )
      ) {
        return
      }
      const head = state.queuedMessages[0]
      queueMicrotask(() => drainQueueHead(send, head.id))
    })
  }, [send])

  // Away-only heads-up for long builds: run finished / failed, or a gate is
  // waiting on the user. Tag coalesces per session so a burst of transitions
  // updates one notification instead of stacking.
  useEffect(() => {
    return useVibeAgentStore.subscribe((state, prev) => {
      const tag = `vibe-${state.sessionId ?? 'session'}`
      if (state.runStatus !== prev.runStatus) {
        if (state.runStatus === 'completed') {
          notifyIfHidden(
            'Vibe Agent: build finished',
            state.queuedMessages.length > 0 && !state.queuePaused
              ? 'Build finished. Your queued message is sending next.'
              : 'The build finished. Come take a look.',
            tag,
          )
        } else if (state.runStatus === 'error') {
          notifyIfHidden('Vibe Agent: build failed', 'The build hit an error and stopped.', tag)
        }
      }
      if (state.gate && state.gate.interactionId !== prev.gate?.interactionId) {
        if (state.gate.kind === 'approval') {
          notifyIfHidden('Vibe Agent needs approval', 'Review the plan to continue the build.', tag)
        } else {
          const count = state.gate.questions.length
          notifyIfHidden(
            'Vibe Agent needs your input',
            count > 1
              ? `Answer ${count} questions to continue the build.`
              : 'Answer a question to continue the build.',
            tag,
          )
        }
      }
    })
  }, [])

  // Queued messages are FE-local by design — reload drops them. Warn instead
  // of faking persistence.
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (useVibeAgentStore.getState().queuedMessages.length === 0) return
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [])

  /** Manual gate answer for a question card. Machine round-trip — no user bubble;
   *  the card itself flips to its answered state. */
  const answerQuestions = useCallback(
    (interactionId: string, answers: GateAnswer[]) => {
      useVibeAgentStore.getState().answerGateLocally(interactionId, answers)
      transport.startRun(
        buildRequestBody(`[FE_EVENT] question_answered ${JSON.stringify(answers)}`),
        viewer,
      )
    },
    [buildRequestBody, viewer],
  )

  /** Manual approval — a visible user turn with the canonical approval text.
   *  send() already clears the gate atomically with the append; approval
   *  gates have no transcript card to resolve. */
  const approve = useCallback(() => {
    send(APPROVAL_MESSAGE)
  }, [send])

  /** Stop the open gate's auto-answer countdown so the user can read at
   *  their own pace. Optimistic (the chip drops on click); the sentinel rides
   *  the ordinary answer channel and the BE re-persists the gate as parked
   *  (run_gates.HOLD_TIMER_MESSAGE — string shared verbatim). Machine
   *  round-trip: no user bubble, gate stays open. */
  const holdGateTimer = useCallback(() => {
    useVibeAgentStore.getState().holdGateLocally()
    transport.startRun(buildRequestBody('[FE_EVENT] hold_gate_timer'), viewer)
  }, [buildRequestBody, viewer])

  /** Server-side cancel; the viewer stays attached for the terminal event. */
  const cancel = useCallback(async () => {
    const { sessionId, responseId } = useVibeAgentStore.getState()
    if (!sessionId || !responseId) return
    try {
      await flowService.cancelWorkflowStream({ session_id: sessionId, response_id: responseId })
    } catch (e) {
      logger.warn('[VIBE] cancel failed', e)
    }
  }, [])

  /** Reset Chat: server-side conversation reset + local wipe. An ACTIVE run
   *  is cancelled first — otherwise the wipe is cosmetic: the run keeps
   *  building, the next send 409s, and the resync resurrects everything the
   *  user just cleared. */
  const reset = useCallback(async () => {
    const id = ref.current.phloId
    const { sessionId, runStatus } = useVibeAgentStore.getState()
    if (isRunLive(runStatus)) {
      await cancel()
    }
    transport.detach()
    useVibeAgentStore.getState().reset()
    if (!id || !sessionId) return
    try {
      await flowService.resetVibeConversation({ phlo_uuid: id, session_id: sessionId })
    } catch (e) {
      logger.warn('[VIBE] conversation reset failed', e)
    }
  }, [cancel])

  // Mount rehydration + flow-switch teardown. Once per flow id; a send never
  // re-triggers it. Rehydrates even with `?new` still in the URL: a reload
  // between the first send and the first draft_saved (which strips the param)
  // has a LIVE detached run behind a "new" flow — skipping the fetch here hid
  // that run entirely. A genuinely new flow just hydrates an empty snapshot.
  // A real→real flow switch (A→B) tears down A's viewer and chat before
  // rehydrating B — a resync must not carry A's pending tail into B or keep
  // A's viewer attached. Never resets on null→id (initial mount / first save).
  useEffect(() => {
    const prevFlowId = lastFlowIdRef.current
    if (prevFlowId && phloId && prevFlowId !== phloId) {
      transport.detach()
      useVibeAgentStore.getState().reset()
      rehydratedFor.current = null
    }
    if (phloId) lastFlowIdRef.current = phloId

    if (!phloId) return
    if (rehydratedFor.current === phloId) return
    rehydratedFor.current = phloId
    autoOpenFor.current = phloId
    void triggerResync()
  }, [phloId, triggerResync])

  // Unmount cleanup: leaving the builder page entirely detaches the viewer and
  // wipes chat state. The A→B in-builder flow switch is already handled by the
  // lastFlowIdRef effect above; this replaces the app-level route-change reset
  // that lived in the deleted legacy route-change handler.
  useEffect(() => {
    aliveRef.current = true
    return () => {
      aliveRef.current = false
      transport.detach()
      useVibeAgentStore.getState().reset()
    }
  }, [])

  return { send, answerQuestions, approve, holdGateTimer, cancel, reset, resync: triggerResync }
}
